'use client';
/**
 * LA FICHE DE POSTE (RQ, 19/08/2026).
 *
 * ── Ce qu'elle est, et ce qu'elle n'est pas ────────────────────────────────
 *
 * « Une fiche de fonction qui décrit en général la fonction de biologiste, et
 * une fiche de poste qui décrit ce que fait le biologiste exactement quand il
 * est en poste. […] La fiche de fonction doit vivre dans la GED car j'ai besoin
 * de l'imprimer dans le dossier d'accueil. […] La fiche de poste sera très
 * vivante et directement reliée à la paillasse, aux plannings et aux
 * habilitations : je veux qu'elle vive dans l'outil. »
 *
 * D'où la forme de cet écran : il n'a AUCUN champ de saisie de contenu. Tout ce
 * qu'il montre est lu ailleurs, et se corrige ailleurs — chaque bloc dit d'où
 * il vient et mène à l'écran qui le règle. Une zone de texte libre « missions
 * du poste » aurait été le réflexe, et la fiche aurait cessé d'être vraie au
 * premier changement de grille.
 *
 * Un seul geste s'y pose : les FONCTIONS admises — plusieurs, « un poste peut
 * être tenu par plusieurs fonctions, par exemple un biologiste à l'accueil ou
 * au tri ». C'est le seul lien qui ne se déduit de rien.
 *
 * ── Ce qui manque est écrit en toutes lettres ──────────────────────────────
 *
 * Une fiche vide ne doit pas se lire « ce poste n'exige rien » mais « personne
 * n'a encore écrit ce qu'il exige » — en audit, c'est toute la différence. Le
 * serveur nomme les manques ; l'écran les met en tête, pas en note de bas de
 * page.
 */
import React from 'react';
import { CATEGORIES_POSTE } from '@/lib/blocs-planning';
import { PanneauEquipementsPoste } from '@/components/screens/PostesEquipements';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import { I, TitrePage, card, EmptyState, RefISO, Skeleton, COUCHES } from '@/components/ui';
import { useSortieFenetre, AlerteSortie } from '@/shared/fenetreModale';
import { FormulairePoste } from '@/components/screens/Rh';
import type { RhPoste } from '@/lib/types';
import { AccesRefuseRh } from '@/components/screens/RhAbsences';

import { Bouton } from '@/shared/Bouton';
import { styleBouton } from '@/shared/boutonStyles';
import { PiedActions } from '@/shared/PiedActions';
/* ── Ce que le serveur rend ──────────────────────────────────────────────── */

interface LigneFiche {
  code: string; libelle: string; section: string; methode: string | null;
  niveau: 'routine' | 'expert'; niveauOrigine: 'routine' | 'expert';
  grilleCode: string; grilleLibelle: string;
}
interface BlocCompetences {
  grilleCode: string; grilleLibelle: string;
  sections: { titre: string; lignes: LigneFiche[] }[];
  nbRoutine: number; nbExpert: number;
}
interface FonctionFiche {
  id: string; label: string; tier?: string | null;
  docCode?: string | null; docTitre?: string | null;
  externalise?: boolean; introuvable?: boolean;
}
export interface FicheDePoste {
  poste: {
    code: string; libelle: string; categorie: string; site: string | null;
    effectifCible: number; modeGarde: string;
    valableDu: string | null; valableAu: string | null; actif: boolean;
    debut: string | null; fin: string | null; calendrierResume: string;
  };
  fonctions: FonctionFiche[];
  exigences: {
    code: string; libelle: string; connue: boolean; niveauMin: string | null;
    criteres?: { titre: string; lignes: LigneFiche[] }[];
    nbCriteres?: number;
  }[];
  competences: BlocCompetences[];
  nbCompetences: number;
  /** les gestes réellement à démontrer — cf. `nbCriteres`, qui compte les partages */
  nbCriteresDistincts?: number;
  equipements: { code: string; nom: string; origine: 'pose' | 'habilitation'; habilitation?: string; statut?: string | null }[];
  equipementsIntrouvables: string[];
  tenuPar: { nom: string; passages: number; dernier: string }[];
  depuis: string;
  etat: { manques: string[]; complete: boolean };
  descriptif: FicheDescriptive[];
  descriptifResume: string;
  rappel: string;
}

/** Une tâche du document, et le renvoi vers la grille quand elle y figure déjà. */
interface TacheDecrite { texte: string; dejaEnGrille: boolean; grille: string | null }

/**
 * Le descriptif : ce que le document signé porte et qu'aucun registre ne dit.
 *
 * ⚠️ Il NE REMPLACE PAS les compétences, qui restent dérivées de la grille.
 * C'est pourquoi il s'affiche dans un bloc distinct, sous elles, et que chaque
 * tâche déjà écrite en grille le dit : sans cette marque, on lirait deux fois la
 * même exigence et l'on croirait le poste deux fois plus lourd.
 */
interface FicheDescriptive {
  code: string; fichier: string; designation: string;
  /** Les champs étiquetés que l'export Word avait collés au titre. */
  precisions: string[];
  certitude: 'certain' | 'propose';
  motifRattachement: string;
  rubriques: { cle: string; libelle: string; lignes: (string | TacheDecrite)[] }[];
  tachesPropres: number;
  rubriquesAbsentes: string[];
  remarques: string[];
}

/* Même liste que le planning et le formulaire (30/08/2026) : celle qui vivait
   ici en nommait quatre, dont deux que le formulaire n'offrait pas. */
const CATEGORIES: Record<string, string> = Object.fromEntries(
  CATEGORIES_POSTE.map((c) => [c.cle, c.libelle]),
);

/** Un bloc de la fiche, avec la mention de SA source. Chaque bloc la porte :
 *  c'est ce qui empêche qu'on vienne un jour « compléter » la fiche à la main
 *  au lieu de corriger le registre qui l'alimente. */
/**
 * UNE HABILITATION EXIGÉE, ET SES CRITÈRES SOUS LE PLI.
 *
 * ── ⚠️ CE QUI RESTE VISIBLE PLI FERMÉ ────────────────────────────────────
 *
 * Le nom, le niveau minimum, le nombre de critères, et l'alerte quand le code
 * ne désigne rien. Replier ne doit jamais replier un AVERTISSEMENT : une
 * exigence qui ne correspond à aucune habilitation du référentiel ne bloque
 * personne au planning, et c'est précisément ce qu'il faut voir sans cliquer.
 *
 * ── ⚠️ « 0 critère » SE DIT ──────────────────────────────────────────────
 *
 * Une habilitation sans critère est dépliable sur du vide, et le vide se lirait
 * « rien à démontrer » alors qu'il veut dire « personne n'a écrit ce qu'il faut
 * démontrer ». Le pli reste alors fermé et la phrase prend sa place.
 */
function ExigenceDepliable({ e }: {
  e: {
    code: string; libelle: string; connue: boolean; niveauMin: string | null;
    criteres?: { titre: string; lignes: LigneFiche[] }[];
    nbCriteres?: number;
  };
}) {
  const [ouvert, setOuvert] = React.useState(false);
  const n = e.nbCriteres ?? 0;
  const nExpert = (e.criteres || []).reduce(
    (t, s) => t + s.lignes.filter((l) => l.niveau === 'expert').length, 0,
  );

  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
      <button
        onClick={() => n && setOuvert((v) => !v)}
        disabled={!n}
        aria-expanded={n ? ouvert : undefined}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
          padding: '9px 11px', border: 0, background: ouvert ? 'var(--gray-25)' : 'transparent',
          cursor: n ? 'pointer' : 'default', font: 'inherit',
        }}
      >
        {/* Le chevron ne tourne que s'il y a quelque chose à ouvrir. */}
        <span style={{ width: 12, flex: '0 0 12px', color: 'var(--text-muted)', fontSize: 11 }}>
          {n ? (ouvert ? '▾' : '▸') : ''}
        </span>
        <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600 }}>
          {e.libelle}
          <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 400, fontSize: 11.5, color: 'var(--text-muted)', marginInlineStart: 8 }}>
            {e.code}
          </span>
        </span>
        {!!e.niveauMin && (
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)', flex: '0 0 auto' }}>
            niveau min. {e.niveauMin}
          </span>
        )}
        <span style={{ fontSize: 11.5, color: n ? 'var(--text-muted)' : 'var(--amber-600)', flex: '0 0 auto' }}>
          {n ? `${n} critère(s)${nExpert ? ` · ${nExpert} expert` : ''}` : 'aucun critère écrit'}
        </span>
      </button>

      {/* L'alerte n'est PAS sous le pli : une exigence qui ne désigne rien ne
          bloque personne, et c'est le genre de chose qu'on ne va pas chercher. */}
      {!e.connue && (
        <p style={{ margin: 0, padding: '0 11px 9px 31px', fontSize: 12, color: 'var(--amber-600)', lineHeight: 1.5 }}>
          Ce code ne correspond à aucune habilitation du référentiel : il n’empêche personne
          d’être affecté ici.
        </p>
      )}
      {!n && e.connue && (
        <p style={{ margin: 0, padding: '0 11px 9px 31px', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Aucune tâche de grille ne porte cette habilitation — ce n’est pas « rien à démontrer »,
          c’est « rien n’a encore été écrit ».
        </p>
      )}

      {ouvert && !!n && (
        <div style={{ padding: '2px 11px 10px 31px', borderTop: '1px solid var(--border-subtle)' }}>
          {(e.criteres || []).map((s) => (
            <div key={s.titre} style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 3 }}>
                {s.titre}
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6 }}>
                {s.lignes.map((l) => (
                  <li key={l.code}>
                    {l.libelle}
                    {l.niveau === 'expert' && (
                      <span style={{ color: 'var(--amber-600)' }}> — expert</span>
                    )}
                    {!!l.methode && (
                      <span style={{ color: 'var(--text-muted)' }}> · évaluation : {l.methode}</span>
                    )}
                    {/* La grille d'origine reste lisible : les sections de même
                        nom sont fondues ici, mais la tâche se corrige dans SA
                        grille et nulle part ailleurs. */}
                    {!!l.grilleLibelle && (
                      <span style={{ color: 'var(--text-faint)', fontSize: 11.5 }}> · {l.grilleLibelle}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Bloc({ titre, source, lien, children }: {
  titre: string; source: string; lien?: () => void; children: React.ReactNode;
}) {
  return (
    <div style={{ ...card, padding: 18, marginTop: 14 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 14, margin: 0 }}>{titre}</h2>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {source}
          {lien && (
            <>
              {' · '}
              <button onClick={lien} className="fiche-poste-lien"
                style={{ border: 0, background: 'none', padding: 0, font: 'inherit', color: 'var(--cyan-700)', textDecoration: 'underline', cursor: 'pointer' }}>
                y aller
              </button>
            </>
          )}
        </span>
      </div>
      <div style={{ marginTop: 10 }}>{children}</div>
    </div>
  );
}

export function FichePoste() {
  const { rhAcces, rhPostes, selectedPoste, naviguerVers, settings, session, rhHabilitations, savePoste, retirerPoste } = useGed();
  /* Tout se pilote depuis la fiche : le formulaire du poste, la duplication et
     le retrait — plus aucun écran concurrent. */
  const [editePoste, setEditePoste] = React.useState<Partial<RhPoste> | null>(null);
  const [retrait, setRetrait] = React.useState(false);
  const [motif, setMotif] = React.useState('');
  const boiteRetrait = React.useRef<HTMLDivElement | null>(null);
  /* Le motif du retrait est une saisie : la refermer sans l'enregistrer se dit. */
  const refusRetrait = useSortieFenetre({ boite: boiteRetrait, onClose: () => setRetrait(false) });
  const estRQ = session.roles.includes('rq');
  /* Le poste TEL QUE LE MAGASIN le porte : la fiche, elle, ne rend qu'une vue
     assemblée. Ouvrir le formulaire sur cette vue perdrait les champs qu'elle
     n'expose pas — habilitations exigées, composition, équipements. */
  const posteComplet = rhPostes.find((x) => x.code === selectedPoste) || null;
  const [fiche, setFiche] = React.useState<FicheDePoste | null>(null);
  const [erreur, setErreur] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [tour, setTour] = React.useState(0);
  const [editeFonctions, setEditeFonctions] = React.useState<string[] | null>(null);
  /** Les fonctions LUES à l'ouverture de l'édition (14/10/2026) : le moteur
   *  n'applique que ce qui a été coché ou décoché depuis. */
  const fonctionsLues = React.useRef<string[]>([]);
  const [enCours, setEnCours] = React.useState(false);

  const code = selectedPoste || '';

  React.useEffect(() => {
  /* ⚠️ FAUX POSITIF ASSUMÉ de `react-hooks/set-state-in-effect` (02/09/2026).
     Le `setState` synchrone ici n'ajuste pas un état à une entrée : il EFFACE
     la donnée précédente avant d'aller en chercher une autre. Sans lui, l'écran
     afficherait les données de la fiche PRÉCÉDENTE pendant tout le temps de la
     requête — un faux vide à l'envers, qui affirme sur la nouvelle fiche ce qui
     était vrai de l'ancienne. C'est le seul cas où l'on veut ce rendu
     intermédiaire, et il ne se dérive pas : la donnée vient du réseau.
     ⚠️ Silence CIBLÉ, ligne à ligne. */
  // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!code) { setFiche(null); return undefined; }
    let vivant = true;
    setFiche(null);
    void fetch(`/api/ged/ged/rh/postes/${encodeURIComponent(code)}/fiche`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!vivant) return;
        if (j?.ok) { setFiche(j); setErreur(''); }
        else setErreur(`La fiche du poste « ${code} » n’a pas pu être lue.`);
      })
      .catch(() => { if (vivant) setErreur(`La fiche du poste « ${code} » n’a pas pu être lue.`); });
    return () => { vivant = false; };
  }, [code, tour]);

  // La fiche est une LECTURE : elle s'ouvre à qui peut VOIR les postes, pas
  // seulement au gestionnaire RH. Le même partage que le planning — exiger le
  // rôle RH ferait de « qu'attend-on de moi à ce poste ? » une question
  // d'encadrement, alors que c'est celle de la personne qui le tient. Le
  // serveur re-vérifie tout (`exigerSectionRh(ctx, 'postes', 'voir')`).
  const peutLire = rhAcces?.acces || (session.sections?.postes?.voir ?? false);
  if (!peutLire) return <AccesRefuseRh />;

  const orgFonctions = settings?.orgFonctions || [];

  const enregistrerFonctions = async () => {
    if (!editeFonctions) return;
    setEnCours(true);
    const r = await fetchEcriture(`/api/ged/ged/rh/postes/${encodeURIComponent(code)}/fonctions`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fonctions: editeFonctions, base: fonctionsLues.current }),
    });
    const d = await r.json().catch(() => null);
    setEnCours(false);
    if (!r.ok) { setErreur(d?.error?.message || 'Enregistrement refusé.'); return; }
    setErreur(''); setMessage(d?.message || 'Fonctions enregistrées.');
    setEditeFonctions(null); setTour((t) => t + 1);
  };

  /* ── Choisir le poste, quand on arrive sans en avoir désigné un ─────────── */
  if (!code) {
    const postes = rhPostes.filter((p) => p.actif !== false);
    return (
      <section>
        <TitrePage eyebrow={<>Plannings &amp; RH</>} titre="Fiche de poste"
          sousTitre={<>
            Ce qu’on attend de quelqu’un <strong>quand il tient ce poste</strong> — les compétences
            exigées, les appareils, les horaires, et les fonctions qui peuvent l’occuper. <RefISO code="6.2" />
            <br />
            <span style={{ fontSize: 12.5 }}>
              Entièrement <strong>dérivée</strong> : elle n’est ressaisie nulle part, et suit ses sources le
              jour où elles changent.
            </span>
          </>} />
        {!postes.length ? (
          <EmptyState titre="Aucun poste défini"
            indice="Les postes se créent dans « Postes de travail »." />
        ) : (
          <div style={{ ...card, padding: 18, marginTop: 16, display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
            {postes.map((p) => (
              <button key={p.code} style={{ ...styleBouton('contour', 'sm'), textAlign: 'left', padding: '10px 12px', cursor: 'pointer' }}
                onClick={() => naviguerVers('fichePoste', { cle: 'selectedPoste', valeur: p.code })}>
                <strong style={{ fontSize: 13 }}>{p.libelle}</strong>
                <br />
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{p.code}</span>
              </button>
            ))}
          </div>
        )}
      </section>
    );
  }

  if (erreur && !fiche) {
    return (
      <section>
        <div style={{ ...card, padding: 14, marginTop: 12, background: 'var(--red-50)', border: '1px solid #E4A0A0' }}>
          <strong style={{ fontSize: 12.5 }}>{erreur}</strong>
        </div>
      </section>
    );
  }
  if (!fiche) return <Skeleton />;

  const f = fiche;
  const fonctionsAffichees = editeFonctions ?? f.fonctions.map((x) => x.id);

  return (
    <section className="fiche-poste">
      {/* IMPRESSION A4 — la fiche se glisse dans un classeur de paillasse.
          Les boutons et les sélecteurs disparaissent : sur papier, ils ne
          seraient que des rectangles vides. */}
      {/* `no-print` est la classe de la MAISON, définie dans `globals.css`
          (24/08/2026). Cet écran nommait la sienne `sans-impression` et la
          définissait ici : elle marchait, mais recopiée sur un autre écran elle
          n'aurait rien masqué — une classe morte ne se signale jamais. Seule
          reste ci-dessous la part vraiment propre à cette fiche : le format A4
          et l'inversion de visibilité, cet écran n'ayant pas de
          `#ged-print-area` mais s'imprimant en entier. */}
      <style>{`@media print {
        @page { size: A4 portrait; margin: 12mm; }
        body * { visibility: hidden; }
        .fiche-poste, .fiche-poste * { visibility: visible; }
        .fiche-poste { position: absolute; left: 0; top: 0; width: 100%; }
        .fiche-poste button, .fiche-poste select, .fiche-poste input { display: none !important; }
      }`}</style>

      <TitrePage eyebrow={<>Plannings &amp; RH · Fiche de poste</>}
        titre={f.poste.libelle}
        sousTitre={<>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{f.poste.code}</span>
          {' · '}{CATEGORIES[f.poste.categorie] || f.poste.categorie}
          {f.poste.site ? ` · ${f.poste.site}` : ''}
          {!f.poste.actif && <strong style={{ color: 'var(--amber-600)' }}> · poste désactivé</strong>}
          <br />
          <span style={{ fontSize: 12.5 }}>{f.rappel}</span>
        </>}
        actions={<div className="no-print" style={{ display: 'flex', gap: 8 }}>
          <Bouton taille="sm" onClick={() => naviguerVers('fichePoste', { cle: 'selectedPoste', valeur: null })}>
            <I n="back" s={13} /> Retour aux postes
          </Bouton>
          <Bouton taille="sm" onClick={() => window.print()}>
            <I n="print" s={13} /> Imprimer
          </Bouton>
          {/* ⚠️ TOUT SE FAIT ICI — arbitrage de la RQ du 25/08/2026 : « le bouton
              modifier le poste et le clic sur le poste ne donnent pas la même
              chose. Je veux que tout soit regroupé au même endroit. » La liste
              des postes n'ouvre plus de formulaire concurrent. */}
          {estRQ && (
            <>
              <Bouton taille="sm" disabled={!posteComplet} title={posteComplet ? undefined : 'Poste absent du registre chargé — rechargez la page.'} onClick={() => posteComplet && setEditePoste(posteComplet)}>
                <I n="pen" s={13} /> Modifier le poste
              </Bouton>
              {/* ⚠️ « COMPOSER LES TÂCHES » A ÉTÉ RETIRÉ LE 30/08/2026.
                  RQ : « l'écran de composer les tâches ne me sert à rien ; je
                  voudrais plutôt que quand j'ouvre le poste, je vois la liste
                  des habilitations nécessaires. »

                  Le bouton était DÉJÀ mort : l'onglet qu'il visait a été fermé
                  le 29/08, et depuis, il posait `selectedPosteComposition` —
                  que plus aucun écran ne lisait — puis renvoyait sur le module
                  des habilitations, page d'accueil. Un bouton qui navigue
                  quelque part se lit comme un bouton qui marche : personne ne
                  l'aurait signalé.

                  Les compétences du poste se DÉDUISENT désormais de ses
                  habilitations exigées, et se lisent sur cette fiche même, dans
                  le bloc dépliable ci-dessous. */}
              {/* Dupliquer : le code est vidé exprès — deux postes ne peuvent pas
                  le partager, et laisser l'ancien ferait échouer l'enregistrement
                  au dernier moment, après la saisie. */}
              <Bouton taille="sm" disabled={!posteComplet} title={posteComplet ? undefined : 'Poste absent du registre chargé — rechargez la page.'} onClick={() => posteComplet && setEditePoste({
                  ...posteComplet, documentId: undefined, code: '',
                  libelle: `${f.poste.libelle} (copie)`,
                })}>
                <I n="archive" s={13} /> Dupliquer
              </Bouton>
            </>
          )}
          {/* Retirer un poste touche au PLANNING (ses affectations) : il relève
              du seul RH (RQ, 03/10/2026), comme le serveur — et non du RQ. */}
          {rhAcces?.acces && (
            <Bouton taille="sm" onClick={() => setRetrait(true)}>
              <I n="trash" s={13} /> Retirer
            </Bouton>
          )}
        </div>} />

      {/* Retirer un poste : un MOTIF, pas une confirmation.
          ⚠️ « Êtes-vous sûr ? » ne laisse aucune trace ; un motif se relit six
          mois plus tard, quand personne ne se souvient pourquoi le poste a
          disparu du planning. Le moteur en exige cinq caractères au minimum. */}
      {retrait && (
        <div role="dialog" aria-modal="true" aria-label="Retirer ce poste"
          style={{ position: 'fixed', inset: 0, zIndex: COUCHES.TRAVAIL, background: 'rgba(0,0,0,.35)', display: 'grid', placeItems: 'center', padding: 20 }}
          {...refusRetrait.proprietesVoile}>
          <div ref={boiteRetrait} {...refusRetrait.proprietesBoite}
            style={{ ...card, padding: 20, maxWidth: 520, width: '100%', display: 'grid', gap: 12, position: 'relative', ...refusRetrait.proprietesBoite.style }}>
            {refusRetrait.alerte && <AlerteSortie onRester={refusRetrait.rester} onQuitter={refusRetrait.quitterSansEnregistrer} />}
            <h2 style={{ margin: 0, fontSize: 16 }}>Retirer le poste « {f.poste.libelle} »</h2>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Le poste est <strong>désactivé</strong>, pas effacé : les affectations passées restent
              lisibles. Qui tenait quelle paillasse tel jour est exactement ce qu’un audit vient
              vérifier.
            </p>
            <label style={{ fontSize: 12.5, fontWeight: 600 }} htmlFor="motifRetrait">Motif du retrait</label>
            <input id="motifRetrait" value={motif} onChange={(e) => setMotif(e.target.value)}
              placeholder="Poste fusionné avec P3 — Biochimie"
              style={{ padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', fontSize: 13 }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Bouton taille="sm" onClick={() => setRetrait(false)}>Annuler</Bouton>
              <Bouton taille="sm" disabled={motif.trim().length < 5} onClick={async () => {
                  const ok = await retirerPoste(f.poste.code, motif.trim());
                  if (ok) { setRetrait(false); setMotif(''); naviguerVers('fichePoste', { cle: 'selectedPoste', valeur: null }); }
                }}>
                Retirer le poste
              </Bouton>
            </div>
          </div>
        </div>
      )}

      {editePoste && (
        <FormulairePoste
          poste={editePoste}
          tousLesPostes={rhPostes}
          disciplines={(settings?.disciplines || []).filter((d) => d.active !== false).map((d) => d.name)}
          habilitations={rhHabilitations}
          onFermer={() => setEditePoste(null)}
          onEnregistrer={async (p) => {
            const ok = await savePoste(p);
            if (!ok) return;
            setEditePoste(null);
            /* Une duplication crée un AUTRE poste : la fiche doit suivre le
               nouveau, sinon on croit avoir modifié celui qu'on regardait. */
            if (p.code && p.code !== f.poste.code) {
              naviguerVers('fichePoste', { cle: 'selectedPoste', valeur: p.code });
            }
          }}
        />
      )}

      {!!message && (
        <div className="no-print" style={{ ...card, padding: 14, marginTop: 12, background: 'var(--green-50)', border: '1px solid #1F7A45' }}>
          <strong style={{ fontSize: 12.5, color: 'var(--green-600)' }}>{message}</strong>
        </div>
      )}
      {!!erreur && (
        <div className="no-print" style={{ ...card, padding: 14, marginTop: 12, background: 'var(--red-50)', border: '1px solid #E4A0A0' }}>
          <strong style={{ fontSize: 12.5 }}>{erreur}</strong>
        </div>
      )}

      {/* CE QUI MANQUE, EN TÊTE. Placé ici et non en bas : une fiche
          incomplète lue en diagonale se prendrait pour une fiche complète. */}
      {!f.etat.complete && (
        <div style={{ ...card, padding: 16, marginTop: 16, background: 'var(--amber-50)', border: '1px solid var(--amber-600)' }}>
          <strong style={{ fontSize: 13 }}>Cette fiche est incomplète — et voici ce qui manque</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6 }}>
            {f.etat.manques.map((m) => <li key={m}>{m}</li>)}
          </ul>
        </div>
      )}

      {/* ── Le cadre du poste ───────────────────────────────────────────── */}
      <Bloc titre="Le poste" source="réglé dans « Postes de travail »"
        lien={() => naviguerVers('postes')}>
        <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 18px', fontSize: 12.5, margin: 0 }}>
          <dt style={{ color: 'var(--text-muted)' }}>Horaires</dt>
          <dd style={{ margin: 0 }}>{f.poste.debut || '—'} – {f.poste.fin || '—'}</dd>
          <dt style={{ color: 'var(--text-muted)' }}>Effectif attendu</dt>
          <dd style={{ margin: 0 }}>{f.poste.effectifCible} personne{f.poste.effectifCible > 1 ? 's' : ''}</dd>
          <dt style={{ color: 'var(--text-muted)' }}>Jours d’ouverture</dt>
          <dd style={{ margin: 0, color: f.poste.calendrierResume ? undefined : 'var(--amber-600)' }}>
            {f.poste.calendrierResume || 'non définie'}
          </dd>
          {f.poste.modeGarde && f.poste.modeGarde !== 'aucun' && (<>
            <dt style={{ color: 'var(--text-muted)' }}>Garde</dt>
            <dd style={{ margin: 0 }}>{f.poste.modeGarde}</dd>
          </>)}
          {(f.poste.valableDu || f.poste.valableAu) && (<>
            <dt style={{ color: 'var(--text-muted)' }}>Validité</dt>
            <dd style={{ margin: 0 }}>{f.poste.valableDu || '…'} → {f.poste.valableAu || '…'}</dd>
          </>)}
        </dl>
      </Bloc>

      {/* ── Les fonctions : LE seul geste de cet écran ───────────────────── */}
      <Bloc titre="Qui peut tenir ce poste"
        source="fonctions de l’organigramme — plusieurs sont possibles">
        {!orgFonctions.length ? (
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
            L’organigramme fonctionnel ne porte aucune fonction : rien à rattacher pour l’instant.
          </p>
        ) : editeFonctions ? (
          <div className="no-print">
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 8px', lineHeight: 1.5 }}>
              Cochez toutes les fonctions qui peuvent occuper ce poste. Un biologiste à l’accueil ou au
              tri, cela arrive : c’est bien une liste, pas un choix unique.
            </p>
            <div style={{ display: 'grid', gap: 4, gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
              {orgFonctions.map((o) => (
                <label key={o.id} style={{ fontSize: 12.5, display: 'flex', gap: 7, alignItems: 'center', cursor: 'pointer' }}>
                  <input type="checkbox" checked={fonctionsAffichees.includes(o.id)}
                    onChange={(e) => setEditeFonctions(e.target.checked
                      ? [...fonctionsAffichees, o.id]
                      : fonctionsAffichees.filter((x) => x !== o.id))} />
                  {o.label}
                </label>
              ))}
            </div>
            <PiedActions taille="sm" style={{ marginTop: 12 }} annuler={{ onClick: () => setEditeFonctions(null) }} principale={{ libelle: "Enregistrer les fonctions", disabled: enCours, onClick: () => void enregistrerFonctions() }} />
          </div>
        ) : (
          <>
            {!f.fonctions.length ? (
              <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: 0, lineHeight: 1.5 }}>
                Aucune fonction déclarée. Tant que ce lien manque, la fiche de poste ne renvoie vers
                aucune <strong>fiche de fonction</strong> — le document du dossier d’accueil.
              </p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7 }}>
                {f.fonctions.map((x) => (
                  <li key={x.id}>
                    {x.introuvable ? (
                      <span style={{ color: 'var(--amber-600)' }}>
                        {x.id} — cette fonction n’existe plus dans l’organigramme
                      </span>
                    ) : (
                      <>
                        <strong>{x.label}</strong>
                        {x.externalise && <span style={{ color: 'var(--text-muted)' }}> · externalisée</span>}
                        {/* ⚠️ LA FICHE DE FONCTION N'EST PAS UN DOCUMENT DE LA GED
                            (corrigé le 30/08/2026). Elle vit dans `org_fonctions`
                            et s'édite dans l'écran Organigramme. Ce bouton
                            ouvrait `doc` avec le code de la fiche : la GED ne le
                            connaissant pas, il ne menait nulle part — et la
                            mention « le document n'existe pas encore » faisait
                            croire à un document manquant plutôt qu'à un lien mal
                            dirigé. */}
                        {x.docCode ? (
                          <>
                            {' — '}
                            <button className="no-print"
                              onClick={() => naviguerVers('org', { cle: 'selectedCode', valeur: x.docCode })}
                              style={{ border: 0, background: 'none', padding: 0, font: 'inherit', color: 'var(--cyan-700)', textDecoration: 'underline', cursor: 'pointer' }}>
                              fiche de fonction {x.docCode}
                            </button>
                            {x.docTitre && (
                              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{` (${x.docTitre})`}</span>
                            )}
                          </>
                        ) : (
                          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                            {' '}— aucune fiche de fonction rattachée dans l’organigramme
                          </span>
                        )}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <Bouton taille="sm" className="no-print" onClick={() => { fonctionsLues.current = f.fonctions.map((x) => x.id); setEditeFonctions(f.fonctions.map((x) => x.id)); }} style={{ marginTop: 10 }}>
              <I n="pen" s={12} /> Modifier les fonctions
            </Bouton>
          </>
        )}
      </Bloc>

      {/* ── UN SEUL BLOC : les habilitations, dépliables ──────────────────
          RQ, 30/08/2026 : « quand j'ouvre le poste, je vois la liste des
          habilitations nécessaires ; en cliquant sur l'habilitation, je peux
          dérouler ses différents critères. C'est trop un bloc, ce qui apparaît
          maintenant. »

          Il y avait deux blocs qui disaient la même chose par deux bouts : un
          mur de quatre-vingt-quatorze lignes groupées par GRILLE, et tout en
          bas quatre puces nommant les habilitations. Les lignes venaient
          pourtant de ces habilitations — le regroupement par grille effaçait
          l'origine en chemin.

          ⚠️ Le bloc reste EN TÊTE de ce qui bloque : ces habilitations
          empêchent l'affectation au planning. Replier les critères ne doit pas
          replier ce fait, d'où le sous-titre, toujours visible. */}
      <Bloc titre={`Habilitations exigées pour être affecté ici — ${f.exigences.length}`}
        source="elles BLOQUENT l’affectation au planning · critères tirés des grilles"
        lien={() => naviguerVers('rhhab')}>
        {!f.exigences.length ? (
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
            Aucune : le poste n’empêche personne d’y être affecté. Cela peut être voulu — mais rien
            ne le dit, et un auditeur posera la question.
          </p>
        ) : (
          <>
            <div style={{ display: 'grid', gap: 6 }}>
              {f.exigences.map((e) => (
                <ExigenceDepliable key={e.code} e={e} />
              ))}
            </div>
            {/* ⚠️ LES DEUX CHIFFRES, ET POURQUOI ILS DIFFÈRENT. Le manuel de
                prélèvement sert l'accueil, le prélèvement et le tri : il figure
                sous chacune des trois habilitations, parce qu'il définit chacune
                des trois. Mais la personne ne le démontre qu'une fois. Ne
                montrer que la somme ferait croire le poste plus lourd qu'il
                n'est ; ne montrer que le distinct ferait croire les
                habilitations plus légères. */}
            {typeof f.nbCriteresDistincts === 'number' && (
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '12px 0 0', lineHeight: 1.6 }}>
                {f.nbCriteresDistincts} geste(s) distinct(s) à démontrer en tout.
                {f.exigences.reduce((n, e) => n + (e.nbCriteres || 0), 0) > f.nbCriteresDistincts && (
                  <> Le total des listes ci-dessus est plus élevé : certaines tâches
                  définissent plusieurs habilitations et se démontrent une seule fois.</>
                )}
              </p>
            )}
          </>
        )}
      </Bloc>

      {/* ⚠️ LE REPLI, ET SEULEMENT LUI. Quand un poste n'exige AUCUNE
          habilitation, ses compétences viennent encore de la `composition`
          posée à la main. Ce bloc n'apparaît que dans ce cas — sinon il
          redirait, groupé par grille, ce que les habilitations viennent de
          dire habilitation par habilitation. */}
      {!f.exigences.length && !!f.competences.length && (
        <Bloc titre={`Compétences posées à la main — ${f.nbCompetences}`}
          source="composition du poste, en l’absence d’habilitation exigée"
          lien={() => naviguerVers('rhhab')}>
          <div style={{ display: 'grid', gap: 14 }}>
            {f.competences.map((b) => (
              <div key={b.grilleCode}>
                <h3 style={{ fontSize: 12.5, margin: '0 0 6px', display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  {b.grilleLibelle}
                  <span style={{ fontWeight: 400, fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {b.nbRoutine} de routine{b.nbExpert ? ` · ${b.nbExpert} expert` : ''}
                  </span>
                </h3>
                {b.sections.map((s) => (
                  <div key={s.titre} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.03em', margin: '6px 0 3px' }}>
                      {s.titre}
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6 }}>
                      {s.lignes.map((l) => <li key={l.code}>{l.libelle}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Bloc>
      )}

      {/* ── Le descriptif : les fiches signées ──────────────────────────── */}
      {!!f.descriptif?.length && (
        <Bloc titre={`Ce que dit la fiche signée — ${f.descriptif.length} document(s)`}
          source="fiches de poste du laboratoire (qa/RH Personnel), transcrites">
          <div style={{ display: 'grid', gap: 18 }}>
            {f.descriptif.map((d) => (
              <div key={d.code}>
                <h3 style={{ fontSize: 12.5, margin: '0 0 4px', display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  {d.designation || d.code}
                  <span style={{ fontWeight: 400, fontSize: 12.5, color: 'var(--text-muted)' }}>{d.code}</span>
                  {/* ⚠️ Une proposition qu'on ne distingue pas d'un fait se lit
                      comme un fait dès la relecture suivante. */}
                  {d.certitude === 'propose' && (
                    <span title={d.motifRattachement}
                      style={{
                        fontSize: 11, fontWeight: 500, padding: '1px 7px', borderRadius: 'var(--radius-pill)',
                        background: 'var(--surface-raised)', color: 'var(--text-muted)',
                        border: '1px solid var(--border-subtle)',
                      }}>
                      rattachement proposé, à valider
                    </span>
                  )}
                </h3>
                {!!d.precisions?.length && (
                  <p style={{ margin: '0 0 6px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
                    {d.precisions.join(' · ')}
                  </p>
                )}
                {d.certitude === 'propose' && (
                  <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                    {d.motifRattachement}
                  </p>
                )}
                {d.rubriques.map((r) => (
                  <div key={r.cle} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.03em', margin: '6px 0 3px' }}>
                      {r.libelle}
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.6 }}>
                      {r.lignes.map((l, i) => {
                        const t = typeof l === 'string' ? null : l;
                        return (
                          <li key={i} style={t?.dejaEnGrille ? { color: 'var(--text-muted)' } : undefined}>
                            {typeof l === 'string' ? l : l.texte}
                            {t?.dejaEnGrille && (
                              <span style={{ fontSize: 12.5, marginLeft: 6 }}>
                                — déjà exigée en grille{t.grille ? ` (${t.grille})` : ''}
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
                {(!!d.remarques.length) && (
                  <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
                    {d.remarques.map((m) => <li key={m}>{m}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Bloc>
      )}


      {/* ── Les habilitations exigées ────────────────────────────────────── */}

      {/* ── Les appareils ────────────────────────────────────────────────── */}
      <Bloc titre="Les appareils du poste"
        source="posés à la main, ou déduits des habilitations exigées">
        {!f.equipements.length ? (
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
            Aucun appareil rattaché à ce poste.
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7 }}>
            {f.equipements.map((e) => (
              <li key={e.code}>
                {e.nom} <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>{e.code}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  {e.origine === 'habilitation'
                    ? ' — on ne peut pas tenir le poste sans être habilité dessus'
                    : ' — rattaché à la main'}
                </span>
              </li>
            ))}
          </ul>
        )}
        {!!f.equipementsIntrouvables.length && (
          <p style={{ fontSize: 12.5, color: 'var(--amber-600)', margin: '8px 0 0', lineHeight: 1.5 }}>
            {f.equipementsIntrouvables.length} code(s) rattaché(s) ne correspondent à aucune fiche
            d’équipement ({f.equipementsIntrouvables.join(' · ')}) : signalés, jamais effacés.
          </p>
        )}
        {/* ⚠️ RECUEILLI ICI LE 30/08/2026, en fermant « Composition des tâches ».
            Ce panneau — rattacher un appareil au poste, depuis le poste — vivait
            dans cet écran-là. Supprimer l'écran l'aurait emporté avec lui, et le
            laboratoire aurait perdu le seul endroit où l'on rattache un appareil
            EN PARTANT DU POSTE (l'autre sens, depuis la fiche de l'appareil,
            existe toujours).

            Ce n'est pas un déplacement de confort : c'est la règle du 25/08 —
            « je veux que tout soit regroupé au même endroit » — appliquée à ce
            qui restait dehors. */}
        {estRQ && <PanneauEquipementsPoste posteCode={f.poste.code} posteLibelle={f.poste.libelle} />}
      </Bloc>

      {/* ── Qui l'a tenu ─────────────────────────────────────────────────── */}
      <Bloc titre="Qui l’a tenu ces six derniers mois"
        source={`d’après le planning, depuis le ${f.depuis}`}
        lien={() => naviguerVers('planning')}>
        {!f.tenuPar.length ? (
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
            Personne n’a été affecté à ce poste sur la période. Un poste que le planning n’ouvre
            jamais se relit autrement qu’un poste tenu tous les jours — c’est un constat, pas un reproche.
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7 }}>
            {f.tenuPar.map((t) => (
              <li key={t.nom}>
                {t.nom} — {t.passages} passage{t.passages > 1 ? 's' : ''}
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}> · dernier le {t.dernier}</span>
              </li>
            ))}
          </ul>
        )}
      </Bloc>

      {/* ⚠️ CE PIED DE PAGE S'IMPRIME (30/08/2026). Il était `no-print` : la
          feuille sortait donc SANS DATE, SANS NOM et sans la phrase qui dit
          qu'elle n'est pas versionnée.

          C'est exactement l'inverse de ce qu'il faut. Sur l'écran, on sait
          qu'on lit un état vivant — il suffit de recharger. Sur PAPIER, plus
          rien ne le dit : la feuille circule, vieillit, et se lit comme un
          document maîtrisé qu'elle n'est pas. Un auditeur qui trouve une fiche
          de poste non datée dans un classeur y voit un document non maîtrisé ;
          datée et marquée « instantané », elle est irréprochable.

          C'est le même piège que le livret d'accueil imprimé : ce qui quitte
          l'outil doit porter le jour où il l'a quitté. */}
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '14px 2px 0', lineHeight: 1.6 }}>
        Fiche éditée le {fmtDate(new Date().toISOString())}
        {session?.name ? ` par ${session.name}` : ''}. Elle n’est pas versionnée et n’a pas à l’être :
        elle affiche l’état du jour de ses sources. La <strong>fiche de fonction</strong>, elle, est un
        document de la GED, versionné et imprimable dans le dossier d’accueil.
      </p>
    </section>
  );
}
